import { memo, useMemo } from 'react';
import { useIntl } from 'react-intl';
import LaunchOutlined from '@mui/icons-material/LaunchOutlined';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Drawer, Typography, useMediaQuery } from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import keycloak from '../../../Keycloak';
import MenuList from '../MenuList';
import LogoSection from '../LogoSection';
import MiniDrawerStyled from './MiniDrawerStyled';

import LAYOUT_CONST from 'constant';
import useConfig from 'hooks/useConfig';
import { drawerWidth } from 'store/constant';

import { useDispatch, useSelector } from 'store';
import { openDrawer } from 'store/slices/menu';

// ==============================|| SIDEBAR DRAWER ||============================== //

const Sidebar = () => {
    const theme = useTheme();
    const intl = useIntl();
    const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));
    const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

    const dispatch = useDispatch();
    const { drawerOpen } = useSelector((state) => state.menu);

    const { drawerType, locale } = useConfig();
    const previousLogin = keycloak.tokenParsed?.previousLogin;

    const formattedPreviousLogin = useMemo(() => {
        if (!previousLogin || typeof previousLogin !== 'string') {
            return null;
        }

        const parsedDate = new Date(previousLogin);

        if (Number.isNaN(parsedDate.getTime())) {
            return null;
        }

        try {
            return new Intl.DateTimeFormat(locale, {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(parsedDate);
        } catch {
            return new Intl.DateTimeFormat('es-ES', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }).format(parsedDate);
        }
    }, [previousLogin, locale]);

    const logo = useMemo(
        () => (
            <Box sx={{ display: 'flex', p: 2 }}>
                {matchDownMd && drawerOpen ? (
                    <LogoSection />
                ) : (
                    <Box
                        component="span"
                        sx={{
                            display: { xs: 'none', md: 'block' },
                            flexGrow: 1,
                            textAlign: 'center',
                            minHeight: '40px'
                        }}
                    ></Box>
                )}
            </Box>
        ),
        [matchDownMd, drawerOpen]
    );
    const drawer = useMemo(
        () => (
            <PerfectScrollbar
                component="div"
                style={{
                    height: !matchUpMd ? 'calc(100vh - 56px)' : 'calc(100vh - 88px)',
                    paddingLeft: drawerOpen ? '16px' : 0,
                    paddingRight: drawerOpen ? '16px' : 0,
                    marginTop: drawerOpen ? 0 : '20px'
                }}
                options={{ suppressScrollX: true, wheelPropagation: false }}
            >
                <MenuList />

                {drawerOpen && (
                    <Box
                        component="a"
                        href="https://www.sede.fnmt.gob.es/certificados/persona-fisica"
                        target="_blank"
                        rel="noopener noreferrer"
                        display="flex"
                        flexDirection="row"
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                            mt: 12,
                            color: theme.palette.grey[900],
                            cursor: 'pointer',
                            textDecoration: 'none',
                            '&:hover': {
                                color: theme.palette.primary.main
                            }
                        }}
                    >
                        <Typography variant="subtitle1" mr={1} sx={{ color: 'inherit' }}>
                            {intl.formatMessage({ id: 'OBTENER_CERTIFICADO' })}
                        </Typography>
                        <LaunchOutlined sx={{ fontSize: "1.1rem" }} />
                    </Box>
                )}
                {drawerOpen && (
                    <Box
                        component="a"
                        href="https://rubricae.com/accesibilidad/"
                        target="_blank"
                        rel="noopener noreferrer"
                        display="flex"
                        flexDirection="row"
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                            mt: 2,
                            color: theme.palette.grey[900],
                            cursor: 'pointer',
                            textDecoration: 'none',

                            '&:hover': {
                                color: theme.palette.primary.main
                            }
                        }}
                    >
                        <Typography variant="subtitle1" mr={1} sx={{ color: 'inherit', fontSize: '12px' }}>
                            {intl.formatMessage({ id: 'ACCESIBILIDAD' })}
                        </Typography>
                        <LaunchOutlined sx={{ fontSize: "0.8rem" }} />
                    </Box>
                )}

                {drawerOpen && (
                    <Box
                        sx={{
                            width: '100%',
                            textAlign: 'center',
                            color: theme.palette.text.secondary,
                            fontSize: 12,
                            mt: 12,
                            mb: 2
                        }}
                    >
                        {formattedPreviousLogin && (
                            <Typography component="p" variant="caption" sx={{ display: 'block', color: 'inherit', mb: 0.5 }}>
                                {intl.formatMessage({ id: 'ULTIMA_SESION' })}: {formattedPreviousLogin}
                            </Typography>
                        )}
                        <Typography component="p" variant="caption" sx={{ display: 'block', color: 'inherit' }}>
                            {intl.formatMessage({ id: 'VERSION' })} : {__APP_VERSION__}
                        </Typography>
                    </Box>
                )}
            </PerfectScrollbar>
        ),
        [matchUpMd, drawerOpen, drawerType, theme.palette.primary.main, intl, formattedPreviousLogin]
    );

    return (
        <Box
            component="nav"
            sx={{ flexShrink: { md: 0 }, width: matchUpMd ? drawerWidth : 'auto' }}
            aria-label={intl.formatMessage({ id: 'MENU_PRINCIPAL' })}
        >
            {matchDownMd || (drawerType === LAYOUT_CONST.MINI_DRAWER && drawerOpen) ? (
                <Drawer
                    variant={matchUpMd ? 'persistent' : 'temporary'}
                    anchor="left"
                    open={drawerOpen}
                    onClose={() => dispatch(openDrawer(!drawerOpen))}
                    sx={{
                        '& .MuiDrawer-paper': {
                            mt: matchDownMd ? 0 : 11,
                            zIndex: 1099,
                            width: drawerWidth,
                            background: theme.palette.background.default,
                            color: theme.palette.text.primary,
                            borderRight: 'none'
                        }
                    }}
                    ModalProps={{ keepMounted: true }}
                    color="inherit"
                >
                    {matchDownMd && logo}

                    {drawer}
                </Drawer>
            ) : (
                <MiniDrawerStyled variant="permanent" open={drawerOpen}>
                    {logo}

                    {drawer}
                </MiniDrawerStyled>
            )}
        </Box>
    );
};

export default memo(Sidebar);
